import React from 'react';
import PropTypes from 'prop-types';
import PreferencesSection from '../PreferencesSection';
import css from './ExternalCalendarSyncPreferences.scss';
import Checkbox from 'SF/Checkbox';
import { StaffProfilePrivileges, StaffNotificationPreferences } from 'utils/types';
import { getWord } from 'utils/tenant';
import Icon from 'SF/MaterialIcon';
import ExchangeSyncPreferences from './ExchangeSyncPreferences';

function StarfishCalSync({ mailNewApp, mailNewBlock, setValue }) {
    return (
        <div className={css['starfish-calendar-sync']}>
            <label id="starfishCalendarSync">Email me calendar attachments for every:</label>
            <div className={css['email-alert-group']} aria-labelledby="starfishCalendarSync">
                <div>
                    <Checkbox ariaLabel="Appointment change"
                              checked={mailNewApp}
                              onChange={(checked) => setValue({ mailNewApp: checked })} />
                    <span className={css['starfish-calendar-sync-label']}>Appointment change</span>
                </div>
                <div>
                    <Checkbox ariaLabel={`Change to my ${getWord('officeHours')} and ${getWord('groupSessions')}`}
                              checked={mailNewBlock}
                              onChange={(checked) => setValue({ mailNewBlock: checked })} />
                    <span className={css['starfish-calendar-sync-label']}>
                            Change to my {getWord('officeHours')} and {getWord('groupSessions')}
                    </span>
                </div>
            </div>
        </div>
    );
}

StarfishCalSync.propTypes = {
    mailNewApp: PropTypes.bool,
    mailNewBlock: PropTypes.bool,
    setValue: PropTypes.func
}

// External Calendar Sync Preference settings for Staff.
// Staff can choose their exchange and/or google calendar to be synced with Starfish calendar.
// They can also choose to receive emails in their external calendar for changes in their Starfish calendar.
const ExternalCalendarSyncPreferences = ({ hasSchedule, exchangeAccounts, exchangeFreeBusyPopupLink, googleFreeBusyPopupRouteLink,
                                             privileges, notificationPreferences, setValue, externalCalendarPreferenceSync }) => {

    const getFreeBusyLink = (freeBusyPopupLink, linkName) => {
        return (
            <span dangerouslySetInnerHTML={{
                __html: `<a href="${freeBusyPopupLink}" aria-label="Click here for further instructions on ${linkName} sync" aria-haspopup="true">Click here</a>`
            }}/>
        );
    };

    const getGoogleInfo = () => {
        return (
            <div className={css['warning-text']}>
                <Icon icon="warning" className={css['warning']}/>
                <span className={css['important-text']}>Important:</span> You must share your private calendar link below with {getWord('Starfish')}.
                <p>{getFreeBusyLink(googleFreeBusyPopupRouteLink, 'google calendar')} for further instructions.</p>
            </div>);
    };

    const googleCalendarSync = () => {
        if (!privileges.googleIntegrationEnabled || !hasSchedule) return null;
        return (
            <div className={css['external-cal-sync']}>
                <label className={css['external-sync-label']}>Google Calendar Sync</label>
                {getGoogleInfo()}
                <div className={css['external-sync-checkbox']}>
                    <Checkbox id="googleCal"
                              ariaLabel=""
                              checked={!!notificationPreferences.googleFreeBusyEnabled}
                              onChange={(checked) => setValue({ googleFreeBusyEnabled: checked })} />
                    <label htmlFor="googleCal" className={css['allow-busy-times-sync']}>
                        Allow {getWord('Starfish')} to read busy times <span className={css['semi-bold']}>from</span> my Google Calendar</label>
                </div>
                <div className={css['google-calendar']}>
                    <label id="googleCalLink">Paste Google Calendar Link</label>
                    <input type="text"
                           aria-labelledby="googleCalLink"
                           disabled={!notificationPreferences.googleFreeBusyEnabled}
                           value={notificationPreferences.googleCalUrl}
                           onChange={(e) => setValue({ googleCalUrl: e.target.value })} />
                </div>
            </div>
        );
    }

    const getExternalCalendarSyncs = () => {
        if (!(privileges.googleIntegrationEnabled || privileges.exchangeIntegrationEnabled) || !hasSchedule) return null;
        return (
            <>
                <h4>External Calendar Sync</h4>
                <p className={css['calendar-sync-text']}>
                    Sync busy times
                    <span className={css['semi-bold']}> from </span> your External Calendar
                    <span className={css['semi-bold']}> to </span> your {getWord('Starfish')} Calendar
                </p>
                {privileges.exchangeIntegrationEnabled && hasSchedule
                    && <ExchangeSyncPreferences exchangeAccounts={exchangeAccounts}
                                                exchangeFreeBusyPopupLink={exchangeFreeBusyPopupLink}
                                                notificationPreferences={notificationPreferences}
                                                privileges={privileges}
                                                setValue={setValue}
                                                externalCalendarPreferenceSync={externalCalendarPreferenceSync} />}
                {googleCalendarSync()}
            </>
        );
    };

    return (
        <PreferencesSection isStaff
                            headerText={"Calendar Sync"}
                            descriptionText={`Sync calendar items between your external calendar to your ${getWord('Starfish')} calendar`}>
            <h4>{getWord('Starfish')} Calendar Sync</h4>
            <p className={css['calendar-sync-text']}>Select options to sync
                <span className={css['semi-bold']}> from</span> your {getWord('Starfish')} Calendar
                <span className={css['semi-bold']}> to</span> your External Calendar
            </p>
            <StarfishCalSync mailNewApp={!!notificationPreferences.mailNewApp}
                             mailNewBlock={!!notificationPreferences.mailNewBlock}
                             setValue={(calendarSyncSettings) => setValue({...notificationPreferences, ...calendarSyncSettings})} />
             {getExternalCalendarSyncs()}
        </PreferencesSection>
    )
};

ExternalCalendarSyncPreferences.propTypes = {
    hasSchedule: PropTypes.bool,
    exchangeAccounts: PropTypes.string,
    exchangeFreeBusyPopupLink: PropTypes.string,
    googleFreeBusyPopupRouteLink: PropTypes.string,
    privileges: StaffProfilePrivileges,
    notificationPreferences: StaffNotificationPreferences,
    setValue: PropTypes.func,
    externalCalendarPreferenceSync: PropTypes.func
};

export default ExternalCalendarSyncPreferences;

